/**
 * inquire-modal.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Geo-gates the PDP Add-to-Cart button for non-Poland visitors.
 *
 * Behaviour:
 *  • Fetches the visitor's country code via ipapi.co (free, no key needed).
 *  • Poland (PL) → no change; the normal Add-to-Cart button stays visible.
 *  • All other countries → hides Add-to-Cart, shows the "Inquire" button,
 *    which opens the inquiry modal.
 *  • The modal can be closed via the (CLOSE) button, the backdrop, or Escape.
 *  • Focus is trapped inside the modal while it is open.
 *  • If the geo request fails, the default Add-to-Cart is preserved (safe fallback).
 *
 * Dependencies: none — vanilla JS, works alongside Dawn's existing product-form.js.
 */

(function () {
  'use strict';

  /* ── Config ────────────────────────────────────────────────────────────── */
  const STORE_COUNTRIES = ['PL', 'DE', 'NL', 'BE']; // Countries with direct checkout
  const GEO_API_URL   = 'https://ipapi.co/json/';
  const GEO_TIMEOUT   = 5000;           // ms — fall back gracefully if slow

  /* ── Helpers ───────────────────────────────────────────────────────────── */

  /** Fetch with a timeout so a slow geo API never blocks the page. */
  function fetchWithTimeout(url, ms) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return fetch(url, { signal: controller.signal })
      .finally(() => clearTimeout(timer));
  }

  /** Return all inquire modals on the page (one per section, typically one). */
  function getAllModals() {
    return Array.from(document.querySelectorAll('.inquire-modal'));
  }

  /** Return all focusable elements inside a container. */
  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
        'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  /* ── Modal class ───────────────────────────────────────────────────────── */

  class InquireModal {
    constructor(modal) {
      this.modal       = modal;
      this.overlay     = modal.querySelector('[data-inquire-close]');
      this.closeButtons = modal.querySelectorAll('[data-inquire-close]');
      this.previouslyFocused = null;

      this._onKeyDown  = this._onKeyDown.bind(this);
      this._bindClose();
    }

    open() {
      this.previouslyFocused = document.activeElement;
      this.modal.removeAttribute('hidden');
      document.body.classList.add('inquire-modal-open');
      document.addEventListener('keydown', this._onKeyDown);

      // Move focus to first focusable element
      const focusable = getFocusable(this.modal);
      if (focusable.length) focusable[0].focus();
    }

    close() {
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove('inquire-modal-open');
      document.removeEventListener('keydown', this._onKeyDown);

      if (this.previouslyFocused && this.previouslyFocused.focus) {
        this.previouslyFocused.focus();
      }
    }

    _bindClose() {
      this.closeButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          // Don't close if the click was inside the panel (only overlay clicks)
          if (btn.classList.contains('inquire-modal__overlay')) {
            this.close();
          } else {
            this.close();
          }
        });
      });

      // Clicking the overlay (outside the panel) closes the modal
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target.dataset.inquireClose !== undefined) {
          this.close();
        }
      });
    }

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }

      // Focus trap
      if (e.key === 'Tab') {
        const focusable = getFocusable(this.modal);
        if (!focusable.length) return;

        const first = focusable[0];
        const last  = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }
  }

  /* ── Geo check + button swap ───────────────────────────────────────────── */

  function swapButtonsForInternational() {
    // Show Inquire button, hide Add-to-Cart
    document.querySelectorAll('.inquire-modal__trigger').forEach((btn) => btn.style.display = '');
    document.querySelectorAll('.product-form__submit[name="add"]').forEach((btn) => btn.style.display = 'none');

    // Also hide dynamic checkout buttons (Buy Now / Shop Pay etc.)
    document.querySelectorAll('.shopify-payment-button').forEach((el) => el.style.display = 'none');

    // Wire up each Inquire button to its corresponding modal
    document.querySelectorAll('.inquire-modal__trigger').forEach((btn) => {
      // The section id is encoded in the button's id: "InquireButton-{section_id}"
      const sectionId = btn.id.replace('InquireButton-', '');
      const modalEl   = document.getElementById('InquireModal-' + sectionId);
      if (!modalEl) return;

      const modalInstance = new InquireModal(modalEl);

      btn.addEventListener('click', () => modalInstance.open());

      // If the page was loaded with ?contact_posted=true (after form submit redirect),
      // re-open the modal so the success message is visible
      if (window.location.search.includes('contact_posted=true') ||
          modalEl.querySelector('.inquire-modal__success')) {
        modalInstance.open();
      }
    });
  }

  async function initGeoGate() {
    // Keep buttons invisible until we know the country
    const addButtons = document.querySelectorAll('.product-form__submit[name="add"]');
    addButtons.forEach((btn) => {
      btn.style.visibility = 'hidden';
      btn.style.pointerEvents = 'none';
    });

    try {
      const response = await fetchWithTimeout(GEO_API_URL, GEO_TIMEOUT);

      if (!response.ok) throw new Error('Geo API response not OK');

      const data = await response.json();
      const countryCode = (data.country_code || '').toUpperCase();

      if (countryCode && !STORE_COUNTRIES.includes(countryCode)) {
        swapButtonsForInternational();
      }
      // If PL or empty/unknown, fall through to restore visibility below
    } catch (_err) {
      // Network error or timeout — fail open: show Add-to-Cart
    } finally {
      // Restore visibility for whichever button is now the active one
      addButtons.forEach((btn) => {
        // Only un-hide if it wasn't replaced by the inquire button
        if (btn.style.display !== 'none') {
          btn.style.visibility = '';
          btn.style.pointerEvents = '';
        }
      });
    }
  }

  /* ── Boot ──────────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGeoGate);
  } else {
    initGeoGate();
  }
})();